/**
 * Scenario-related selectors.
 *
 * These selectors are defensive: they validate the expected state shape and
 * return `null` when scenario data is missing or invalid.
 */

/**
 * Safely gets the scenario object from the Redux state.
 *
 * @param {object} state - Redux state
 * @returns {object|null} Scenario object if present, otherwise null
 */
const getScenario = state => {
    if (!state || typeof state !== "object") return null;

    const scenario = state?.inputData?.present?.generalSettings?.scenario;
    return scenario && typeof scenario === "object" ? scenario : null;
};

/**
 * Selects the scenario name.
 *
 * @param {object} state - Redux state
 * @returns {string|null} Scenario name or null if unavailable
 */
export const selectScenarioName = state => {
    const scenario = getScenario(state);
    const name = scenario?.scenarioName;
    return typeof name === "string" && name.trim() !== "" ? name : null;
};

/**
 * Selects the scenario id.
 *
 * @param {object} state - Redux state
 * @returns {string|number|null} Scenario id or null if unavailable
 */
export const selectScenarioId = state => {
    const scenario = getScenario(state);
    const id = scenario?.scenarioId;

    if (typeof id === "string") return id.trim() !== "" ? id : null;
    if (typeof id === "number" && Number.isFinite(id)) return id;

    return null;
};

/**
 * Selects the full scenario info object.
 *
 * @param {object} state - Redux state
 * @returns {object|null} Scenario object or null if unavailable
 */
export const selectScenarioInfo = state => getScenario(state);
/**
 * Whether a save or rename of a scenario is still waiting on the server. Loading
 * another scenario is paused until it finishes (see uiReducer).
 *
 * @param {object} state - Redux state
 * @returns {boolean}
 */
export const selectScenarioWriteInFlight = state =>
    Boolean(state?.ui?.scenarioWritesInFlight > 0);

/** Why a scenario's load button is disabled during a save. */
export const LOAD_PAUSED_DURING_SAVE_TITLE = "Finishing your save. You can load a scenario in a moment.";

/** Why a Save button is disabled: a save is already out, and it isn't a second one. */
export const SAVE_IN_PROGRESS_TITLE = "Saving. You can save again in a moment.";
